import { ReactNode, createContext, useContext, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { BetslipTicketType } from '../@types';
import useLocalization from '../hooks/useLocalization';
import BetslipBridge from '../providers/BetslipBridge';
import { getBetslipState, useBetslipStore } from '../stores/betslip/betslipStore';
import type { BetslipEvent, ErrorMessageOddsChangePayload } from '../types/betslip';
import { WalletType } from '../types/enums';

// Betslip state lives in the zustand store (src/stores/betslip). This module is a compatibility
// layer that preserves the original context API while consumers migrate to the granular selectors
// in src/stores/betslip/selectors.ts. Unlike the old context, a consumer of this hook still
// re-renders on every betslip state change — hot-path components should use the selectors instead.
export type { BetslipEvent, ErrorMessageOddsChangePayload };

type BetslipContextData = {
  bets: BetslipEvent[];
  addBet: (bet: BetslipEvent) => boolean;
  addOrRemoveBet: (bet: BetslipEvent) => void;
  removeBet: (outcomeId: string) => void;
  removeAllBets: () => void;
  updateSingleBetOdds: ({ outcomeId, odds, oddsChange }: ErrorMessageOddsChangePayload) => void;
  isOutcomeSelected: (outcomeId: string) => boolean;
  toggleBanker: (outcomeId: string) => void;
  errors: string[];
  clearErrors: () => void;
  uniqueEventCount: number;
  isWaysTicket: boolean;
  resetBankers: () => void;
  currentBankerCount: number;
  isMaxBankerCountReached: boolean;
  infoMessages: Set<string>;
  insertCopiedBetslip: (bets: BetslipEvent[]) => void;
  updateSinglesStakeAmount: (outcomeId: string, amount: string) => void;
  resetSinglesStakeAmounts: () => void;
  reofferedBettingSlipsIds: string[];
  removeReofferedBettingSlip: (bettingSlipId: string) => void;
  betslipTicketType: BetslipTicketType;
  selectedWallet: WalletType;
  setSelectedWallet: (wallet: WalletType) => void;
  hasBetBuilderSelection: boolean;
};

type BetslipProviderProps = {
  children: ReactNode;
};

const BetslipProviderGuardContext = createContext(false);

export const BetslipProvider = ({ children }: BetslipProviderProps) => {
  const hasParentProvider = useContext(BetslipProviderGuardContext);

  if (hasParentProvider) {
    throw new Error('BetslipProvider cannot be nested');
  }

  return (
    <BetslipProviderGuardContext.Provider value>
      <BetslipBridge />
      {children}
    </BetslipProviderGuardContext.Provider>
  );
};

export const useBetslipContext = (): BetslipContextData => {
  const hasProvider = useContext(BetslipProviderGuardContext);
  const { t } = useLocalization();

  if (!hasProvider) {
    throw new Error('useBetslipContext must be used within a BetslipProvider');
  }

  const slice = useBetslipStore(
    useShallow((state) => ({
      bets: state.bets,
      selectedOutcomeKeys: state.selectedOutcomeKeys,
      errorMessageKeys: state.errorMessageKeys,
      uniqueEventCount: state.uniqueEventCount,
      isWaysTicket: state.isWaysTicket,
      currentBankerCount: state.currentBankerCount,
      isMaxBankerCountReached: state.isMaxBankerCountReached,
      infoMessageKeys: state.infoMessageKeys,
      reofferedBettingSlipsIds: state.reofferedBettingSlipsIds,
      betslipTicketType: state.betslipTicketType,
      selectedWallet: state.selectedWallet,
      hasBetBuilderSelection: state.hasBetBuilderSelection,
    }))
  );

  return useMemo(() => {
    // Actions are stable for the lifetime of the store — reading them via getState() here does
    // not subscribe to anything.
    const store = getBetslipState();

    return {
      bets: Object.values(slice.bets),
      addBet: store.addBet,
      addOrRemoveBet: store.addOrRemoveBet,
      removeBet: store.removeBet,
      removeAllBets: store.removeAllBets,
      updateSingleBetOdds: store.updateSingleBetOdds,
      isOutcomeSelected: (outcomeId: string) => slice.selectedOutcomeKeys.has(outcomeId),
      toggleBanker: store.toggleBanker,
      errors: slice.errorMessageKeys.map((key) => t(key)),
      clearErrors: store.clearErrors,
      uniqueEventCount: slice.uniqueEventCount,
      isWaysTicket: slice.isWaysTicket,
      resetBankers: store.resetBankers,
      currentBankerCount: slice.currentBankerCount,
      isMaxBankerCountReached: slice.isMaxBankerCountReached,
      infoMessages: new Set(Array.from(slice.infoMessageKeys, (key) => t(key))),
      insertCopiedBetslip: store.insertCopiedBetslip,
      updateSinglesStakeAmount: store.updateSinglesStakeAmount,
      resetSinglesStakeAmounts: store.resetSinglesStakeAmounts,
      reofferedBettingSlipsIds: slice.reofferedBettingSlipsIds,
      removeReofferedBettingSlip: store.removeReofferedBettingSlip,
      betslipTicketType: slice.betslipTicketType,
      selectedWallet: slice.selectedWallet,
      setSelectedWallet: store.setSelectedWallet,
      hasBetBuilderSelection: slice.hasBetBuilderSelection,
    };
  }, [slice, t]);
};
